import type { CreateMcpConnection } from '@sbc/domain-models/agents';
import { type FormEvent, useState } from 'react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { PasswordInput } from '@/components/ui/password-input';

export interface ConnectionFormValues {
  id: string;
  name: string;
  url: string;
  enabled: boolean;
  maxConcurrentCalls: number | null;
}

const EMPTY: ConnectionFormValues = {
  id: '',
  name: '',
  url: '',
  enabled: true,
  maxConcurrentCalls: null,
};

interface ConnectionFormProps {
  initial?: ConnectionFormValues;
  // On edit, indicates whether a token is already stored (blank field = unchanged).
  bearerTokenSet?: boolean;
  submitting: boolean;
  submitLabel: string;
  error?: string | null;
  onSubmit: (input: CreateMcpConnection) => void;
  onCancel: () => void;
}

function tokenHint(isEdit: boolean, isSet: boolean): string {
  if (!isEdit) {
    return 'Stored encrypted; never shown again.';
  }
  return isSet ? 'Configured. Leave blank to keep the current value.' : 'Not configured yet.';
}

export function ConnectionForm({
  initial,
  bearerTokenSet,
  submitting,
  submitLabel,
  error,
  onSubmit,
  onCancel,
}: ConnectionFormProps) {
  const isEdit = initial !== undefined;
  const [values, setValues] = useState<ConnectionFormValues>(initial ?? EMPTY);
  const [bearerToken, setBearerToken] = useState('');

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    onSubmit({
      ...values,
      // Blank token field ⇒ omit ⇒ leave unchanged (edit) / unset (create).
      bearerToken: bearerToken.trim() || undefined,
    });
  };

  return (
    <form className="space-y-4" onSubmit={handleSubmit}>
      <div className="space-y-2">
        <Label htmlFor="id">Id</Label>
        <Input
          id="id"
          required
          disabled={isEdit}
          maxLength={40}
          placeholder="linear"
          value={values.id}
          onChange={(event) => setValues((prev) => ({ ...prev, id: event.target.value }))}
        />
        <p className="text-xs text-[var(--color-muted-foreground)]">
          Lowercase slug; the ref prefix (e.g. `linear`).
        </p>
      </div>

      <div className="space-y-2">
        <Label htmlFor="name">Name</Label>
        <Input
          id="name"
          required
          maxLength={120}
          placeholder="Linear"
          value={values.name}
          onChange={(event) => setValues((prev) => ({ ...prev, name: event.target.value }))}
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="url">URL</Label>
        <Input
          id="url"
          type="url"
          required
          placeholder="https://mcp.linear.app/mcp"
          value={values.url}
          onChange={(event) => setValues((prev) => ({ ...prev, url: event.target.value }))}
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="bearerToken">Bearer token</Label>
        <PasswordInput
          id="bearerToken"
          autoComplete="off"
          placeholder={isEdit && bearerTokenSet ? '••••••••' : ''}
          value={bearerToken}
          onChange={(event) => setBearerToken(event.target.value)}
        />
        <p className="text-xs text-[var(--color-muted-foreground)]">
          {tokenHint(isEdit, bearerTokenSet ?? false)}
        </p>
      </div>

      <div className="space-y-2">
        <Label htmlFor="maxConcurrentCalls">Max concurrent calls</Label>
        <Input
          id="maxConcurrentCalls"
          type="number"
          min={1}
          max={1000}
          placeholder="No limit"
          value={values.maxConcurrentCalls ?? ''}
          onChange={(event) =>
            setValues((prev) => ({
              ...prev,
              maxConcurrentCalls: event.target.value === '' ? null : Number(event.target.value),
            }))
          }
        />
        <p className="text-xs text-[var(--color-muted-foreground)]">
          Shared by every agent using this connection. Leave blank for no limit; a call that arrives
          at the limit waits for a slot.
        </p>
      </div>

      <label className="flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          className="size-4"
          checked={values.enabled}
          onChange={(event) => setValues((prev) => ({ ...prev, enabled: event.target.checked }))}
        />
        Enabled
      </label>

      {error && <p className="text-sm text-[var(--color-destructive)]">{error}</p>}

      <div className="flex items-center gap-2">
        <Button type="submit" disabled={submitting}>
          {submitting ? 'Saving…' : submitLabel}
        </Button>
        <Button type="button" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </form>
  );
}
